import React, { useState, useEffect, ChangeEvent, FormEvent } from "react";
import axios from "axios";
import { useAppSelector } from "@/store/hooks";

const HeaderPage = () => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL; // admin API base
  const { token } = useAppSelector((state) => state.auth);

  const [formData, setFormData] = useState<any>({
    site_name: "",
    meta_title: "",
    meta_description: "",
    meta_keywords: "",
    logo_background: "#fff",
    menu_background: "#261b6c",
    menu_hover: "#3d92d6",
    menu_hover_color: "#fff",
    menu_color: "#fff",
    submenu_background: "#fff",
    submenu_color: "#000",
    signin_color: "#000",
    signin_hover_color: "#fff",
    signin_hover_background: "#3d92d6",
    button_color: "#fff",
    button_background: "#261b6c",
    button_hover_color: "#000",
    button_hover_background: "#3d92d6",
    moblogo_background: "#fff",
    logo: null,
    favicon: null,
  });

  const [previewLogo, setPreviewLogo] = useState<string | null>(null);
  const [previewFavicon, setPreviewFavicon] = useState<string | null>(null);

  // Fetch header details from admin API
useEffect(() => {
  const fetchHeader = async () => {
    try {
      const res = await axios.get(`${baseUrl}/headers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // safe access with optional chaining
      const data = res.data?.data || {};
      setFormData((prev: any) => ({
        ...prev,
        ...data,
        logo: data.logo || null,
        favicon: data.favicon || null,
      }));
      setPreviewLogo(data.logo || null);
      setPreviewFavicon(data.favicon || null);
    } catch (err) {
      console.error("Failed to fetch header:", err);
    }
  };
  fetchHeader();
}, [baseUrl, token]);


  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, files } = e.target;
    if (files && files.length > 0) {
      const file = files[0];
      setFormData((prev: any) => ({ ...prev, [name]: file }));
      if (name === "logo") setPreviewLogo(URL.createObjectURL(file));
      if (name === "favicon") setPreviewFavicon(URL.createObjectURL(file));
    } else {
      setFormData((prev: any) => ({ ...prev, [name]: value }));
    }
  };

 const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
  e.preventDefault();

  if (!token) {
    alert("You are not authorized to update header settings.");
    return;
  }

  try {
    const data = new FormData();

    // Append formData values to FormData
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        // If file, append directly; else append as string
        if (value instanceof File) {
          data.append(key, value);
        } else {
          data.append(key, value.toString());
        }
      }
    });

    // POST request to admin update route
    const res = await axios.post(`${baseUrl}/client/headers/update`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    });

    if (res.data?.success) {
      alert("Header updated successfully!");
      setPreviewLogo(res.data.data.logo || null);
      setPreviewFavicon(res.data.data.favicon || null);
    } else {
      alert(res.data?.error || "Failed to update header.");
    }
  } catch (err: any) {
    console.error(err.response?.data || err.message);
    alert(err.response?.data?.message || "Failed to update header.");
  }
};



  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header & Breadcrumb */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">
          Update Header Settings
        </h1>
        <div className="text-sm text-gray-600">
          <a href="/" className="hover:underline">
            Home
          </a>{" "}
          / <span className="text-green-600 font-medium">Header Details</span>
        </div>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 max-w-7xl mx-auto">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div>
            <label className="block font-semibold text-gray-700 mb-1">Site Name</label>
            <input
              type="text"
              name="site_name"
              value={formData.site_name}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />

            <label className="block font-semibold text-gray-700 mb-1 mt-4">Meta Title</label>
            <input
              type="text"
              name="meta_title"
              value={formData.meta_title}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />

            <label className="block font-semibold text-gray-700 mb-1 mt-4">Meta Description</label>
            <textarea
              name="meta_description"
              value={formData.meta_description}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              rows={3}
            />

            <label className="block font-semibold text-gray-700 mb-1 mt-4">Meta Keywords</label>
            <textarea
              name="meta_keywords"
              value={formData.meta_keywords}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              rows={2}
            />
          </div>

          {/* Logo & Favicon */}
          <div className="flex flex-wrap gap-8 items-start mt-6">
            {/* Logo Upload */}
            <div>
              <label className="block font-semibold text-gray-700 mb-2">Logo</label>
              <input type="file" name="logo" onChange={handleChange} />
              {previewLogo && <img src={previewLogo} alt="logo" className="mt-2 w-24 h-24 object-contain" />}
            </div>

            {/* Favicon Upload */}
            <div>
              <label className="block font-semibold text-gray-700 mb-2">Favicon</label>
              <input type="file" name="favicon" onChange={handleChange} />
              {previewFavicon && <img src={previewFavicon} alt="favicon" className="mt-2 w-12 h-12 object-contain" />}
            </div>
          </div>

          {/* Colors */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            {Object.keys(formData)
              .filter((key) => key.includes("color") || key.includes("background"))
              .map((key) => (
                <div key={key}>
                  <label className="block font-semibold text-gray-700 mb-1 capitalize">{key.replace(/_/g, " ")}</label>
                  <input
                    type="text"
                    name={key}
                    value={formData[key]}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              ))}
          </div>

          {/* Submit */}
          <div className="pt-6">
            <button
              type="submit"
              className="bg-indigo-700 hover:bg-indigo-800 text-white px-6 py-2 rounded-lg shadow-md transition"
            >
              Update
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HeaderPage;
